import { AllTemplatesInfo, TemplateInfo } from '@sr/templates/src';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Box, Button, CheckIcon, Image, Select, Text } from '../../ui';
import API from '../../utils/api';

import './templates-transition.css';
import { Resume, Style, TemplateId } from '@sr/types';

type TemplatePickerProps = {
  initialGroup?: ResumeGroup;
  isSetup?: boolean;
  onSelection?: (style: Style) => void;
  resume: Resume;
};

const TemplatePicker = ({
  initialGroup = 'popular',
  onSelection,
  isSetup,
  resume,
}: TemplatePickerProps) => {
  const [group, setGroup] = useState<ResumeGroup>(initialGroup);
  const [templates, setTemplates] = useState<Array<TemplateInfo>>([]);

  // Filter templates by the selected group
  useEffect(() => {
    const activeGroup = RESUME_GROUPS.find((g) => g.id === group);
    if (!activeGroup || !activeGroup.templates) return;
    const activeTemplates = activeGroup.templates.map(
      (id) => AllTemplatesInfo[id]
    );
    setTemplates(activeTemplates);
  }, [group]);

  // Preload template thumbnails to avoid flickering
  const imgs = [];
  useEffect(() => {
    Object.keys(AllTemplatesInfo).forEach((templateId) => {
      const template = AllTemplatesInfo[templateId as TemplateId];
      const img = new window.Image();
      img.src = template.thumbnailUrl;
      imgs.push(img);
    });
  }, []); // eslint-disable-line

  function handleClick(templateInfo: TemplateInfo) {
    if (resume) {
      API.events.logEvent({
        userId: resume.owner,
        eventName: 'Resume Template Changed',
        properties: {
          'Is Initial': isSetup ? 'True' : 'False',
          'Resume Id': resume.id,
          'Template Id': templateInfo.id,
        },
      });
      const style: Style = {
        activeTemplateId: templateInfo.id,
        customizations: {
          [templateInfo.id]: templateInfo.defaultCustomizations,
          ...resume.style.customizations,
        },
      };
      API.resume.setProperty({
        path: `${resume.id}/style`,
        value: style,
      });
      onSelection && onSelection(style);
    }
  }

  return (
    <Box>
      <MobileGroupPicker group={group} setGroup={setGroup} />
      <Box sx={{ display: 'flex' }}>
        <DesktopGroupPicker group={group} setGroup={setGroup} />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            columnGap: 0,
            rowGap: 40,
            pl: [0, 30, 50],
            justifyContent: 'center',
            flexGrow: 1,
            flexShrink: 1,
            '@media(min-width: 850px)': {
              gridTemplateColumns: '1fr 1fr',
              columnGap: 40,
              rowGap: 30,
            },
            '@media(min-width: 1400px)': {
              gridTemplateColumns: '1fr 1fr 1fr',
              columnGap: 40,
              rowGap: 40,
            },
            '@media(min-width: 1900px)': {
              gridTemplateColumns: '1fr 1fr 1fr',
              columnGap: 60,
              rowGap: 60,
            },
          }}
        >
          <TransitionGroup component={null}>
            {templates.map((templateInfo) => {
              return (
                <CSSTransition
                  key={`${group}-${templateInfo.id}`}
                  classNames="template"
                  timeout={500}
                >
                  <TemplateCard
                    templateInfo={templateInfo}
                    active={
                      !isSetup &&
                      templateInfo.id === resume.style.activeTemplateId
                    }
                    handleClick={() => handleClick(templateInfo)}
                    key={templateInfo.id}
                  />
                </CSSTransition>
              );
            })}
          </TransitionGroup>
        </Box>
      </Box>
    </Box>
  );
};

function DesktopGroupPicker({
  group,
  setGroup,
}: {
  group: ResumeGroup;
  setGroup: (resumeGroup: ResumeGroup) => void;
}) {
  return (
    <Box
      sx={{
        display: ['none', 'block'],
        flexGrow: 0,
        flexShrink: 0,
        width: 210,
        height: '100%',
        position: 'sticky',
        top: 0,
        pt: 16,
      }}
    >
      {RESUME_GROUPS.map((g) => {
        return (
          <Button
            key={g.id}
            onClick={() => setGroup(g.id)}
            data-testid="template-group"
            sx={{
              mb: 2,
              backgroundColor:
                group === g.id ? 'buttonGrayDark' : 'surfaceGrayDark',
              transition: 'background 300ms',
              ':hover': {
                backgroundColor: 'buttonGrayDark',
              },
            }}
          >
            {g.title}
          </Button>
        );
      })}
    </Box>
  );
}

function MobileGroupPicker({
  group,
  setGroup,
}: {
  group: ResumeGroup;
  setGroup: (resumeGroup: ResumeGroup) => void;
}) {
  return (
    <Box
      sx={{
        display: ['block', 'none'],
        'svg path': {
          fill: 'white',
        },
        mb: 6,
      }}
    >
      <Select
        value={group}
        onChange={(e) => setGroup(e.target.value as ResumeGroup)}
        sx={{
          backgroundColor: 'buttonGrayDark',
          color: 'white',
        }}
      >
        {RESUME_GROUPS.map((group) => {
          return (
            <option value={group.id} key={group.id}>
              {group.title}
            </option>
          );
        })}
      </Select>
    </Box>
  );
}

type TemplateProps = {
  active: boolean;
  handleClick: () => void;
  templateInfo: TemplateInfo;
};

const TemplateCard = ({ active, handleClick, templateInfo }: TemplateProps) => (
  <Box
    onClick={handleClick}
    id={`gi-template-card-${templateInfo.id}`}
    data-testid={`pick-template-${templateInfo.id}`}
    sx={{
      textAlign: 'center',
      cursor: 'pointer',
      ':hover .selected': {
        opacity: 1,
      },
      ':hover .check': {
        transform: 'scale(1)',
      },
    }}
  >
    <Box sx={{ position: 'relative' }}>
      <Image
        src={templateInfo.thumbnailUrl}
        sx={{
          width: '100%',
          height: 'auto',
          boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.08)',
        }}
      />
      <Box
        className="selected"
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 400ms',
          backgroundColor: 'rgba(0,0,0, 0.2)',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'blue',
            width: 60,
            height: 60,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'scale(0.8)',
            transition: 'transform 400ms ease-in-out',
          }}
          className="check"
        >
          <CheckIcon color="white" />
        </Box>
      </Box>
    </Box>
    <Text sx={{ marginTop: 12, fontWeight: 'bold', color: 'white' }}>
      {templateInfo.name}
    </Text>
  </Box>
);

type ResumeGroup =
  | 'all'
  | 'popular'
  | 'modern'
  | 'professional'
  | 'bold'
  | 'technical';

type RGroup = {
  id: ResumeGroup;
  title: string;
  templates: Array<TemplateId>;
};

const RESUME_GROUPS: Array<RGroup> = [
  {
    id: 'all',
    title: 'All',
    templates: [
      'san-juan',
      'georgia',
      'pender',
      'parker',
      'seymore',
      'keefer',
      'eton',
      'cordova',
      'yew',
      'venables',
      'arbutus',
      'alexander',
    ],
  },
  {
    id: 'modern',
    title: 'Modern',
    templates: ['georgia', 'pender', 'cordova', 'san-juan'],
  },
  {
    id: 'professional',
    title: 'Professional',
    templates: ['parker', 'venables', 'yew', 'alexander'],
  },
  {
    id: 'technical',
    title: 'Technical',
    templates: ['keefer', 'eton', 'parker'],
  },
  { id: 'bold', title: 'Bold', templates: ['cordova', 'seymore', 'georgia'] },
];

export default TemplatePicker;
